import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@workpay/ds-shared/components/ui/Button';
import { useTranslation } from '@hooks/useTranslation';
import { useBfcacheRestore } from '@hooks/useBfcacheRestore';
import { EorAuthLayout } from './EorAuthLayout';
import { StepTitle, StepSub, BackLink } from './components/StepBits';
import { normalizeSubdomain } from './utils/subdomain';
import { WORKSPACE_DOMAIN_SUFFIX, V3_PORTAL_URL } from '@lib/runtimeConfig';
import { workspaceDiscoveryService } from '@services/workspaceDiscoveryService';
import { redirectToUrl } from '@lib/loginRedirect';
import styles from './EorForm.module.scss';

export function WorkspaceNameEntry() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  // Full-page redirect settles the mutation before the browser leaves — hold a
  // busy state across that gap so the CTA/input don't look re-clickable.
  const [redirecting, setRedirecting] = useState(false);

  const resolve = useMutation({
    mutationFn: (slug: string) => workspaceDiscoveryService.checkWorkspace(slug),
    onSuccess: (res) => {
      if (res.exists && res.active && res.loginUrl) {
        setRedirecting(true);
        redirectToUrl(res.loginUrl);
        return;
      }
      // BE returns a localization key (K_WORKSPACE_NOT_FOUND / _DISABLED /
      // _INVALID_FORMAT) describing why; fall back to a generic line.
      setError(
        res.messageCode
          ? t(res.messageCode, "We couldn't find that workspace. Check the name and try again.")
          : t('K_DS_EXT_EOR_WS_GENERIC_ERROR', 'Something went wrong. Please try again.'),
      );
    },
    onError: () =>
      setError(t('K_DS_EXT_EOR_WS_GENERIC_ERROR', 'Something went wrong. Please try again.')),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const slug = normalizeSubdomain(value);
    if (!slug) {
      setError(t('K_DS_EXT_EOR_WS_REQUIRED', 'Enter your workspace name.'));
      return;
    }
    setError('');
    resolve.mutate(slug);
  }

  // Back after the redirect restores this page from the bfcache with
  // `redirecting` still true — reset so the form is interactive again.
  useBfcacheRestore(() => {
    setRedirecting(false);
    resolve.reset();
  });

  const busy = resolve.isPending || redirecting;

  return (
    <EorAuthLayout>
      <BackLink
        label={t('K_DS_EXT_EOR_BACK_TO_MAIN_LOGIN', 'Not using EOR? Go back to Main Login')}
        onClick={() => {
          // Don't let a second full-page nav race the in-flight workspace redirect.
          if (!busy) redirectToUrl(V3_PORTAL_URL);
        }}
      />
      <StepTitle>{t('K_DS_EXT_EOR_LOGIN_TITLE', 'Log in to EOR')}</StepTitle>
      <StepSub>
        {t('K_DS_EXT_EOR_LOGIN_SUB', "Enter your company's workspace name to get started.")}
      </StepSub>

      <form onSubmit={onSubmit} noValidate>
        <label htmlFor="ws-name" className={styles.label}>
          {t('K_DS_EXT_EOR_WS_LABEL', 'Workspace name')}
        </label>
        <div className={`${styles.inputGroup} ${error ? styles.inputGroupError : ''}`}>
          <input
            id="ws-name"
            type="text"
            autoComplete="off"
            className={styles.input}
            placeholder={t('K_DS_EXT_EOR_WS_PLACEHOLDER', 'yourcompany')}
            value={value}
            disabled={busy}
            onChange={(e) => {
              setValue(e.target.value);
              setError('');
            }}
          />
          <span className={styles.suffix}>{WORKSPACE_DOMAIN_SUFFIX}</span>
        </div>
        {error ? (
          <p role="alert" className={styles.error}>
            {error}
          </p>
        ) : null}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={busy}
          className={styles.cta}
        >
          {t('K_DS_EXT_EOR_CONTINUE', 'Continue')}
        </Button>
      </form>

      <p className={styles.footerText}>
        {t('K_DS_EXT_EOR_DONT_KNOW', "Don't know your workspace?")}{' '}
        <button
          type="button"
          className={styles.link}
          onClick={() => {
            if (!busy) navigate({ to: '/eor/find' });
          }}
        >
          {t('K_DS_EXT_EOR_FIND_VIA_EMAIL', 'Find it via email →')}
        </button>
      </p>
    </EorAuthLayout>
  );
}
